import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { LinkButton, Container, IconView, Flex, BREAKPOINTS } from '@/ui';
import { Button } from '@/components';
import { useForm } from 'react-hook-form';
import { FormInput } from '@/modules/kycModule/components';
import { authApi } from '@/api';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Typography } from 'antd';
import { Title } from '@/ui/typography';
import { COLORS, PASSWORD_REGEX } from '@/utils/static';
import { useTranslation } from 'react-i18next';
import { userReducer } from '@/store/user';
import styled from 'styled-components';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { usePopups } from '@/hooks';

export const ResetPassword = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showSuccessToast, showErrorToast } = usePopups();
  const user = useSelector((state) => state.user);
  const { getValues, control, handleSubmit } = useForm();
  const [codeSentEmail, setCodeSentEmail] = useState((user && user.email) || '');
  const [showPassword, setShowPassword] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);
  const [isSamePasswordError, setIsSamePasswordError] = useState(false);

  useEffect(() => {
    if (user) {
      if (user.id) {
        dispatch(userReducer.setId(user.id));
      }

      if (user.email) {
        sendCode({ email: user.email });
      }
    }
  }, []);

  const sendCode = async (data) => {
    try {
      if (user && user.email) {
        await authApi.changePasswordSendCode(data.email);
      } else {
        await authApi.resetPasswordSendCode(data.email);
      }
      setCodeSentEmail(data.email);
      showSuccessToast(user ? t('Code sent to your email') : t('Code sent'));
    } catch (e) {
      showErrorToast(e, true);
    }
  };

  const resetPassword = async (data) => {
    try {
      if (user && user.email) {
        await authApi.changePassword({
          code: data.code,
          newPassword: data.password
        });
      } else {
        await authApi.resetPassword({
          email: codeSentEmail,
          code: data.code,
          newPassword: data.password
        });
      }
      showSuccessToast(
        user ? t('Password successfully changed') : t('Password successfully reset')
      );
      navigate(user ? '/profile' : '/login');
    } catch (e) {
      showErrorToast(e, true);
    }
  };

  return (
    <StyledMainRow vertical align='center' justify='center'>
      <StyledContainer>
        <Button
          size='large'
          type='text'
          text='Back'
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(-1)}
        />
      </StyledContainer>

      <Container $wide $maxWidth={380}>
        <Title level={2} $textAlign={'center'} $fontWeight={600}>
          {user ? t('Change password') : t('Reset password')}
        </Title>

        {!codeSentEmail && (
          <Form layout='vertical'>
            <FormInput
              label={t('E-mail')}
              form={{ name: 'email', control, rules: { required: t('Required') } }}
              textTransform={'lowercase'}
              marginTopLabel={24}
            />

            <Flex justify='center' $marginTop={24}>
              <Button
                type='filled'
                size='large'
                text={t('Send code')}
                onClick={() => handleSubmit(sendCode)()}
              />
            </Flex>
          </Form>
        )}

        {codeSentEmail && (
          <div>
            {!user && (
              <Container style={{ textAlign: 'left' }} $marginBottom={16}>
                <LinkButton onClick={() => setCodeSentEmail('')}>{t('Change account')}</LinkButton>
              </Container>
            )}

            <Form layout='vertical'>
              <FormInput
                label={t('Code')}
                form={{ name: 'code', control, rules: { required: t('Required') } }}
                marginBottom={16}
              />
              <FormInput
                label={t('New password')}
                form={{
                  name: 'password',
                  control,
                  rules: {
                    required: t('Required'),
                    validate: (v) =>
                      PASSWORD_REGEX.test(v) ||
                      t(
                        'Password must be 8–128 characters long. It can include latin letters, numbers, and special characters'
                      ),
                    deps: ['repeatPassword']
                  }
                }}
                type={showPassword ? 'text' : 'password'}
                icon={<IconView onClick={() => setShowPassword(!showPassword)} />}
                marginTopLabel={24}
              />
              <FormInput
                label={t('Repeat password')}
                form={{
                  name: 'repeatPassword',
                  control,
                  rules: {
                    required: t('Required'),
                    validate: (v) => getValues('password') === v || t('Passwords must match')
                  }
                }}
                type={showPassword2 ? 'text' : 'password'}
                icon={<IconView onClick={() => setShowPassword2(!showPassword2)} />}
                marginTopLabel={24}
              />

              {isSamePasswordError && (
                <StyledTextError>{t('Make a different password')}</StyledTextError>
              )}

              <Flex justify='center' $marginTop={24}>
                <Button
                  type='filled'
                  size='large'
                  text={user ? t('Change password') : t('Reset password')}
                  onClick={() => handleSubmit(resetPassword)()}
                />
              </Flex>
            </Form>
          </div>
        )}
      </Container>
    </StyledMainRow>
  );
};

const StyledMainRow = styled(Flex)`
  min-height: 100vh;
  min-height: 100dvh;

  @media (max-width: ${BREAKPOINTS.smd}px) {
    padding-top: 70px;
  }
`;

const StyledContainer = styled(Container)`
  position: absolute;
  top: 30px;
  left: 60px;
  width: min-content;
  @media (max-width: ${BREAKPOINTS.md}px) {
    top: 15px;
    left: 15px;
  }
`;

const StyledTextError = styled(Typography.Paragraph)`
  margin: 16px 0;
  font-weight: 600;
  font-size: 16px;
  color: ${COLORS.red};
`;
